/**
 * CR-6348: Reworked SO Lines filter panel for the Supplier View.
 *
 * Groups filters into labelled sections (Date Range / Company / Supplier /
 * Classification / Order Details) per the design, re-introduces the legacy ODD
 * Status and OTD Status to LSPD filters, and preserves the existing dependent-
 * filter cascades (Division -> Site, Company Country -> Site -> Warehouse,
 * Supplier Group -> Supplier Site, Commodity Group -> Commodity Type).
 *
 * CR-6348 rework: Added Refresh/Export buttons to header, ClassificationSection
 * for Commodity Group/Type filters, and made OTIF Overridden/PPV visible per
 * client feedback.
 *
 * Rendered only in supplier-portal mode; the customer PO Lines view keeps its
 * existing flat grid (see POLinesFiltersPanel).
 */
import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../ui/card';
import { Button } from '../../../../ui/button';
import { Search, X, Calendar, Building2, Truck, FileText, Tags, RefreshCw, Download, Filter } from 'lucide-react';
import { POLinesFilters, FilterOptions } from '../types';
import { FIELD_CLS, LABEL_CLS, FilterSection } from './fieldStyles';
import { CompanySection } from './sections/CompanySection';
import { SupplierSection } from './sections/SupplierSection';
import { ClassificationSection } from './sections/ClassificationSection';
import { OrderDetailsSection } from './sections/OrderDetailsSection';
import { usePOLinesFilterCascades } from './usePOLinesFilterCascades';

/** Horizontal divider with padding so it doesn't touch card edges */
const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

// Search-helper keys excluded from the "active" count.
const NON_COUNTED_KEYS = new Set(['supplierSearch', 'customerSearch']);

interface SupplierSOLinesFiltersProps {
  filters: POLinesFilters;
  filterOptions: FilterOptions | null;
  onFilterChange: (key: string, value: string) => void;
  onSearch: () => void;
  onClearFilters: () => void;
  // CR-6348 rework: persistent header like customer view - header stays visible,
  // only the filter sections collapse when showFilters is false.
  showFilters: boolean;
  onToggleFilters: () => void;
  onRefresh: () => void;
  onExport: (format: 'xlsx' | 'csv') => void;
  exporting: boolean;
}

export function SupplierSOLinesFilters({
  filters,
  filterOptions,
  onFilterChange,
  onSearch,
  onClearFilters,
  showFilters,
  onToggleFilters,
  onRefresh,
  onExport,
  exporting,
}: SupplierSOLinesFiltersProps) {
  const divisions = filterOptions?.divisions || [];
  const supplierGroups = filterOptions?.supplier_groups || [];
  const siteCountries = filterOptions?.site_countries || [];
  const supplierCountries = filterOptions?.supplier_countries || [];
  const commodityGroups = filterOptions?.commodity_groups || [];
  const commodityTypes = filterOptions?.commodity_types || [];
  // CR-7400: Buyer ID = the ERP buyer codes present on the lines in scope.
  const buyerIdOptions = filterOptions?.buyer_ids || [];

  // CR-6348 rework: count active filters for badge display
  const activeCount = useMemo(
    () =>
      Object.entries(filters).filter(
        ([k, v]) => !NON_COUNTED_KEYS.has(k) && v !== '' && v != null,
      ).length,
    [filters],
  );

  // CR-6348 rework: Commodity Group -> Commodity Type cascade
  const handleCommodityGroupChange = (value: string) => {
    onFilterChange('commodityGroup', value);
    if (value !== filters.commodityGroup) {
      onFilterChange('commodityType', '');
    }
  };

  // Dependent-filter cascades (shared with the customer sectioned panel).
  const {
    companySiteOptions,
    warehouseOptions,
    supplierSiteOptions,
    handleCompanyCountryChange,
    handleDivisionChange,
    handleCompanySiteChange,
    handleSupplierGroupChange,
    handleSupplierCountryChange,
  } = usePOLinesFilterCascades(filters, filterOptions, onFilterChange);

  return (
    <Card>
      <CardHeader className="py-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl">Filter SO Lines</CardTitle>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onExport('xlsx')}
              disabled={exporting}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <button
              type="button"
              onClick={onToggleFilters}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                showFilters
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border hover:bg-muted'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeCount > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  showFilters
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-primary text-primary-foreground'
                }`}>
                  {activeCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </CardHeader>

      {showFilters && (
        <CardContent className="p-0">
        <form onSubmit={(e) => { e.preventDefault(); onSearch(); }} className="space-y-0">
          {/* CR-6347: Sections with shaded headers, icons and dividers matching Pricebooks layout */}
          <div className="[&>section]:border-t-0">
            <SectionDivider />

            {/* Date Range */}
            <FilterSection title="Date Range" icon={Calendar}>
              <div>
                <label className={LABEL_CLS}>ODD From</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="date"
                    value={filters.oddFrom}
                    onChange={(e) => onFilterChange('oddFrom', e.target.value)}
                    className={`${FIELD_CLS} pl-9`}
                  />
                </div>
              </div>
              <div>
                <label className={LABEL_CLS}>ODD To</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="date"
                    value={filters.oddTo}
                    onChange={(e) => onFilterChange('oddTo', e.target.value)}
                    className={`${FIELD_CLS} pl-9`}
                  />
                </div>
              </div>
            </FilterSection>

            <SectionDivider />

            <CompanySection
              filters={filters}
              onFilterChange={onFilterChange}
              siteCountries={siteCountries}
              divisions={divisions}
              companySiteOptions={companySiteOptions}
              warehouseOptions={warehouseOptions}
              onCompanyCountryChange={handleCompanyCountryChange}
              onDivisionChange={handleDivisionChange}
              onCompanySiteChange={handleCompanySiteChange}
              icon={Building2}
            />

            <SectionDivider />

            <SupplierSection
              filters={filters}
              onFilterChange={onFilterChange}
              supplierCountries={supplierCountries}
              supplierGroups={supplierGroups}
              supplierSiteOptions={supplierSiteOptions}
              onSupplierGroupChange={handleSupplierGroupChange}
              onSupplierCountryChange={handleSupplierCountryChange}
              icon={Truck}
            />

            <SectionDivider />

            {/* CR-6348 rework: Added Classification section per client feedback */}
            <ClassificationSection
              filters={filters}
              onFilterChange={onFilterChange}
              commodityGroups={commodityGroups}
              commodityTypes={commodityTypes}
              onCommodityGroupChange={handleCommodityGroupChange}
              icon={Tags}
            />

            <SectionDivider />

            <OrderDetailsSection
              filters={filters}
              onFilterChange={onFilterChange}
              icon={FileText}
              buyerIdOptions={buyerIdOptions}
            />
          </div>

          {/* Footer actions - CR-6347: Clear All left, Apply Filters right */}
          <div className="flex items-center justify-between px-6 py-4">
            <Button type="button" variant="outline" onClick={onClearFilters} className="px-6">
              <X className="h-4 w-4 mr-2" />
              Clear All
            </Button>
            <Button type="submit" className="px-6">
              <Search className="h-4 w-4 mr-2" />
              Apply Filters
            </Button>
          </div>
        </form>
      </CardContent>
      )}
    </Card>
  );
}
